/**
 * DeliveryLogView - Daily order tracker with three tiers:
 *   1. Delivered  — full POD cards (signature, photos, GPS, etc.)
 *   2. On Route   — recipient + address + assigned driver
 *   3. Undelivered — recipient + address only
 *
 * Sorted within each group by internal order number suffix (001, 002, ...).
 * Filterable by date and driver.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import useDeliveryLog from '@/hooks/useDeliveryLog';
import useDeliveryManifestPrint from '@/hooks/useDeliveryManifestPrint';
import { sourceLookupUnavailable } from '@/utils/orderSource';
import {
  MapPin, Loader2, Package, ChevronLeft, ChevronRight,
  Filter, Truck, CircleDot, CheckCircle2, Search, X, Route, AlertTriangle, Printer
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  DeliveredCard,
  DeliveryLogSection as Section,
  FailedCard,
  OnRouteCard,
  UndeliveredCard,
} from '@/components/delivery/DeliveryLogCards';
import {
  collectDeliveryAddresses,
  collectDeliveryDrivers,
  collectDeliverySources,
  filterDeliveryOrders,
  findAddressSuggestions,
  formatDateHeading as fmtDateHeading,
  groupDeliveryOrders,
  orderIdsForRun,
  shiftDate,
  todayString as todayStr,
} from '@/utils/deliveryLog';

// ── helpers ──────────────────────────────────────────────────────────

// ── main component ───────────────────────────────────────────────────

export default function DeliveryLogView() {
  const { sessionBinding } = useAuth();
  // A new accepted login starts a fresh screen, including its search/filter UI.
  return <OwnedDeliveryLog key={`${sessionBinding?.revision}:${sessionBinding?.source}`} />;
}

function OwnedDeliveryLog() {
  const [selectedDate, setSelectedDate] = useState(todayStr());
  const [driverFilter, setDriverFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [runFilter, setRunFilter] = useState('all');
  const [nameSearch, setNameSearch] = useState('');
  const [addressSearch, setAddressSearch] = useState('');
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false);
  const blurTimer = useRef(null);
  const read = useDeliveryLog({ selectedDate, nameSearch, addressSearch });
  const { orders, runs, loading } = read;
  const drivers = useMemo(() => collectDeliveryDrivers(orders), [orders]);

  // Unique sources from loaded orders
  const sources = useMemo(() => collectDeliverySources(orders), [orders]);
  const sourceLookupFailed = orders.some(sourceLookupUnavailable);
  const sourceFilterError = sourceFilter !== 'all' && sourceLookupFailed;
  const [filterNotice, setFilterNotice] = useState('');
  useEffect(() => {
    const removed = [];
    if (!read.orderLoading && !read.orderError && !read.updating) {
      if (driverFilter !== 'all' && !drivers.some(([id]) => id === driverFilter)) {
        setDriverFilter('all'); removed.push('driver');
      }
      if (!sourceLookupFailed && sourceFilter !== 'all' && !sources.includes(sourceFilter)) {
        setSourceFilter('all'); removed.push('source');
      }
    }
    if (!read.runLoading && !read.runError && runFilter !== 'all'
      && !runs.some(run => run.id === runFilter)) {
      setRunFilter('all'); removed.push('run');
    }
    if (removed.length) setFilterNotice(`Unavailable ${removed.join(', ')} filter reset to All.`);
  }, [drivers, sources, runs, driverFilter, sourceFilter, runFilter, sourceLookupFailed,
    read.orderLoading, read.orderError, read.runLoading, read.runError, read.updating]);

  useEffect(() => () => clearTimeout(blurTimer.current), []);
  const ownerKey = useMemo(() => ({ owner: read.owner, orders, runs, ready: read.readyToPrint,
    driverFilter, sourceFilter, runFilter }), [read.owner, orders, runs, read.readyToPrint,
    driverFilter, sourceFilter, runFilter]);
  const { printRun: handlePrintManifest, printDriver: handlePrintDriverManifest,
    printing, printingDriver } = useDeliveryManifestPrint({
    ownerKey, canPrint: read.readyToPrint, isCurrent: read.isCurrent,
    getToken: () => read.captureSession()?.token,
    snapshot: { orders, runs, drivers, selectedDate, runFilter, driverFilter,
      sourceFilter, nameSearch, addressSearch },
  });
  const cardOwner = useMemo(() => ({
    key: read.owner, capture: read.captureSession, isCurrent: read.isCurrent,
  }), [read.owner, read.captureSession, read.isCurrent]);

  // Unique addresses from loaded orders (for autocomplete)
  const uniqueAddresses = useMemo(() => collectDeliveryAddresses(orders), [orders]);

  // Address suggestions filtered by current input
  const addressSuggestions = useMemo(
    () => findAddressSuggestions(uniqueAddresses, addressSearch),
    [addressSearch, uniqueAddresses],
  );

  // Order IDs in the selected run (for run filter)
  const runOrderIds = useMemo(() => orderIdsForRun(runs, runFilter), [runFilter, runs]);

  // Filter — when date is set, apply client-side name/address filters.
  // When date is cleared, server already filtered, just apply driver/source.
  const filtered = useMemo(() => sourceFilterError ? [] : filterDeliveryOrders({
    orders,
    sourceFilter,
    driverFilter,
    drivers,
    runOrderIds,
    nameSearch,
    addressSearch,
    selectedDate,
  }), [orders, driverFilter, sourceFilter, runOrderIds, nameSearch, addressSearch, selectedDate, drivers, sourceFilterError]);

  // Group by status tier
  const { delivered, onRoute, failed, undelivered } = useMemo(
    () => groupDeliveryOrders(filtered),
    [filtered],
  );

  const goBack = () => setSelectedDate((d) => d ? shiftDate(d, -1) : null);
  const goForward = () => setSelectedDate((d) => d ? shiftDate(d, 1) : null);
  const isToday = selectedDate === todayStr();
  const totalOrders = filtered.length;

  return (
    <div className="h-full overflow-y-auto bg-stone-50" data-testid="delivery-log-view">
      <div className="max-w-3xl mx-auto p-4 space-y-4">
        {/* ── Date nav + driver filter ── */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 flex-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={goBack} data-testid="log-date-back" disabled={!selectedDate}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="relative">
              <input
                type="date"
                value={selectedDate || ''}
                onChange={(e) => setSelectedDate(e.target.value || null)}
                className="px-3 py-1.5 pr-8 border border-stone-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                data-testid="log-date-picker"
              />
              {selectedDate && (
                <button
                  onClick={() => setSelectedDate(null)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  data-testid="log-date-clear"
                  title="Clear date — search all dates"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {selectedDate && !isToday && (
              <button onClick={() => setSelectedDate(todayStr())} className="text-xs text-primary font-medium hover:underline" data-testid="log-date-today">
                Today
              </button>
            )}
            {!selectedDate && (
              <button onClick={() => setSelectedDate(todayStr())} className="text-xs text-primary font-medium hover:underline" data-testid="log-date-today">
                Today
              </button>
            )}
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={goForward} data-testid="log-date-forward" disabled={!selectedDate || isToday}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <span className="text-sm font-semibold text-stone-700 ml-1" data-testid="log-date-label">
              {selectedDate ? fmtDateHeading(selectedDate) : 'All dates'}
            </span>
          </div>

          {drivers.length > 0 && (
            <div className="flex items-center gap-2">
              <Filter className="h-3.5 w-3.5 text-stone-400" />
              <select
                value={driverFilter}
                onChange={(e) => setDriverFilter(e.target.value)}
                className="px-3 py-1.5 border border-stone-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                data-testid="log-driver-filter"
              >
                <option value="all">All drivers</option>
                {drivers.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
              {driverFilter !== 'all' && selectedDate && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrintDriverManifest}
                  disabled={printing || printingDriver || !read.readyToPrint}
                  className="h-8 text-xs"
                  data-testid="print-driver-manifest-btn"
                >
                  {printingDriver
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Building…</>
                    : <><Printer className="h-3.5 w-3.5 mr-1" />Print Driver Snapshot</>}
                </Button>
              )}
            </div>
          )}

          {runs.length > 0 && selectedDate && (
            <div className="flex items-center gap-2">
              <Route className="h-3.5 w-3.5 text-stone-400" />
              <select
                value={runFilter}
                onChange={(e) => setRunFilter(e.target.value)}
                className="px-3 py-1.5 border border-stone-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                data-testid="log-run-filter"
              >
                <option value="all">All runs</option>
                {runs.map((r) => (
                  <option key={r.id} value={r.id}>{r.name} ({r.stops?.length || 0})</option>
                ))}
              </select>
              {runFilter !== 'all' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrintManifest}
                  disabled={printing || printingDriver || !read.readyToPrint}
                  className="h-8 text-xs"
                  data-testid="print-run-manifest-btn"
                >
                  {printing
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Building…</>
                    : <><Printer className="h-3.5 w-3.5 mr-1" />Print Run Snapshot</>}
                </Button>
              )}
            </div>
          )}

          {(sources.length > 0 || sourceFilterError) && (
            <div className="flex items-center gap-2">
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="px-3 py-1.5 border border-stone-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                data-testid="log-source-filter"
              >
                <option value="all">All sources</option>
                {sourceFilterError && !sources.includes(sourceFilter) && <option value={sourceFilter}>{sourceFilter}</option>}
                {sources.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* ── Search filters ── */}
        <div className="flex flex-col sm:flex-row gap-2">
          {/* Recipient name search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400 pointer-events-none" />
            <input
              type="text"
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
              placeholder="Search recipient name..."
              className="w-full pl-9 pr-8 py-1.5 border border-stone-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
              data-testid="log-name-search"
            />
            {nameSearch && (
              <button
                onClick={() => setNameSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                data-testid="log-name-clear"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Address search with autocomplete */}
          <div className="relative flex-1">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400 pointer-events-none z-10" />
            <input
              type="text"
              value={addressSearch}
              onChange={(e) => { setAddressSearch(e.target.value); setAddressDropdownOpen(true); }}
              onFocus={() => { clearTimeout(blurTimer.current); if (addressSearch.length >= 2) setAddressDropdownOpen(true); }}
              onBlur={() => { blurTimer.current = setTimeout(() => setAddressDropdownOpen(false), 200); }}
              placeholder="Search address..."
              className="w-full pl-9 pr-8 py-1.5 border border-stone-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
              data-testid="log-address-search"
            />
            {addressSearch && (
              <button
                onClick={() => { setAddressSearch(''); setAddressDropdownOpen(false); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 z-10"
                data-testid="log-address-clear"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            {addressDropdownOpen && addressSuggestions.length > 0 && (
              <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg overflow-hidden" data-testid="log-address-dropdown">
                {addressSuggestions.map((a, i) => (
                  <button
                    key={a.full}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-stone-50 transition-colors flex items-center gap-2 border-b border-stone-100 last:border-0"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setAddressSearch(a.street);
                      setAddressDropdownOpen(false);
                    }}
                    data-testid={`log-address-option-${i}`}
                  >
                    <MapPin className="h-3.5 w-3.5 text-stone-400 flex-shrink-0" />
                    <span className="truncate">{a.full}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Summary ── */}
        <p className="text-xs text-stone-500" data-testid="log-snapshot-scope">
          Prints use loaded records for the selected run or driver, not every screen filter.
          They are snapshots, not guaranteed complete run/day reports.
        </p>
        {orders.length >= 200 && <p role="status" data-testid="log-limit-warning" className="text-sm text-amber-700">
          The 200-order limit was reached. Additional matching orders may exist.
        </p>}
        {filterNotice && <p role="status" data-testid="log-filter-notice" className="text-sm text-amber-700">{filterNotice}</p>}
        {sourceFilterError && <div role="alert" data-testid="log-source-filter-error">
          <p>Source filter could not be applied. Your selection is unchanged.</p>
          <Button variant="outline" onClick={read.retry}>Retry source lookup</Button>
        </div>}
        {(read.orderError || read.runError) && <div role="alert" data-testid="log-read-error" className="text-sm text-red-700">
          {read.orderError && <p>Unable to load orders. {orders.length ? 'Showing previously loaded records for this query.' : 'Results are unavailable.'}</p>}
          {read.runError && <p>Unable to load runs. This does not mean there are no runs.</p>}
          <Button variant="outline" size="sm" onClick={read.retry} data-testid="log-retry">Retry</Button>
        </div>}
        {read.runLoading && selectedDate && <p role="status" className="text-sm text-stone-500">Loading runs…</p>}
        {!sourceFilterError && <div className="flex items-center gap-4 text-sm text-stone-500" data-testid="log-summary">
          <span>{totalOrders} loaded order{totalOrders !== 1 ? 's' : ''}</span>
          {delivered.length > 0 && <span className="text-green-600">{delivered.length} delivered</span>}
          {onRoute.length > 0 && <span className="text-blue-600">{onRoute.length} on route</span>}
          {failed.length > 0 && <span className="text-red-600">{failed.length} failed</span>}
          {undelivered.length > 0 && <span className="text-stone-400">{undelivered.length} pending</span>}
        </div>}
        {/* ── Content ── */}
        {loading || read.updating ? (
          <div className="flex items-center justify-center py-16 text-stone-400">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Updating results...
          </div>
        ) : sourceFilterError || ((read.orderError || read.runError) && totalOrders === 0) ? null : totalOrders === 0 ? (
          <Card className="p-10 text-center">
            <Package className="h-10 w-10 mx-auto mb-3 text-stone-300" />
            {!selectedDate && !nameSearch && !addressSearch ? (
              <>
                <p className="text-stone-500 font-medium">Search across all dates</p>
                <p className="text-sm text-stone-400 mt-1">Type a recipient name or address to find orders</p>
              </>
            ) : (
              <>
                <p className="text-stone-500 font-medium">No orders found</p>
                <p className="text-sm text-stone-400 mt-1">
                  {selectedDate ? `No results for ${fmtDateHeading(selectedDate)}` : 'Try a different search term'}
                </p>
              </>
            )}
          </Card>
        ) : (
          <div className="space-y-5">
            {/* Delivered section */}
            {delivered.length > 0 && (
              <Section icon={CheckCircle2} label="Delivered" count={delivered.length} color="green">
                {delivered.map((o) => <DeliveredCard key={o.id} order={o} owner={cardOwner} />)}
              </Section>
            )}

            {/* On Route section */}
            {onRoute.length > 0 && (
              <Section icon={Truck} label="On Route" count={onRoute.length} color="blue">
                {onRoute.map((o) => <OnRouteCard key={o.id} order={o} />)}
              </Section>
            )}

            {/* Failed section */}
            {failed.length > 0 && (
              <Section icon={AlertTriangle} label="Failed" count={failed.length} color="red">
                {failed.map((o) => <FailedCard key={o.id} order={o} />)}
              </Section>
            )}

            {/* Undelivered section */}
            {undelivered.length > 0 && (
              <Section icon={CircleDot} label="Pending" count={undelivered.length} color="stone">
                {undelivered.map((o) => <UndeliveredCard key={o.id} order={o} />)}
              </Section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
